import { GitBranch, Handshake, History } from 'lucide-react'
import { useMemo } from 'react'
import { useAgenciesLookupQuery } from '@/features/agencies/hooks/use-agencies-query'
import { useCompaniesLookupQuery } from '@/features/companies/hooks/use-companies-query'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { ScrollFade } from '@/shared/components/scroll-fade/scroll-fade'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { FEATURES } from '@/shared/config/features'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { useClientDetailQuery } from '../../hooks/use-client-detail-query'
import type { IClientAdminRow } from '../../models/client-admin'
import { ClientDetailActions } from './client-detail-actions'
import { ClientDetailSummary } from './client-detail-summary'
import { ClientFlowsTab } from './client-flows-tab'
import { ClientHistoryTab } from './client-history-tab'
import { ClientNegotiationsTab } from './client-negotiations-tab'

interface ClientDetailPanelProps {
  client: IClientAdminRow
}

/**
 * Container of the client side-panel. Orchestrates the data (detail + company /
 * agency lookups + permissions) and composes the presentational pieces: summary,
 * actions and the Negociaciones / Flujos tabs. Each concern lives in its own module.
 */
export function ClientDetailPanel({ client }: ClientDetailPanelProps) {
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const { data: detail, isLoading, error, refetch } = useClientDetailQuery(client._id)
  const { data: companies = [] } = useCompaniesLookupQuery()
  const { data: agencies = [] } = useAgenciesLookupQuery()

  const companyName = useMemo(
    () => companies.find((c) => c.id === detail?.company)?.name ?? null,
    [companies, detail?.company]
  )
  const agencyName = useMemo(
    () => agencies?.find((a) => a.id === detail?.agency)?.name ?? null,
    [agencies, detail?.agency]
  )

  return (
    <div className="flex flex-col">
      {isLoading ? (
        <SidePanelSection separated={false}>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-11 w-2/3" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        </SidePanelSection>
      ) : !detail ? (
        /*
          Sin carga en curso y sin detalle, hay dos caminos que terminan acá: la consulta falló,
          o respondió sin `client` —`useClientDetailQuery` devuelve `null` en ese caso—. Los dos
          significan que este panel no tiene nada que mostrar, y ninguno de los dos es "cargando":
          antes ambos caían en el skeleton de arriba y lo dejaban girando para siempre.
        */
        <SidePanelSection separated={false}>
          <QueryErrorState error={error} resource="el expediente" onRetry={() => void refetch()} />
        </SidePanelSection>
      ) : (
        <>
          <SidePanelSection>
            <SidePanelSectionBody className="gap-4">
              <ClientDetailSummary
                name={client.name}
                photo={client.photo}
                detail={detail}
                companyName={companyName}
                agencyName={agencyName}
              />
            </SidePanelSectionBody>
          </SidePanelSection>

          <SidePanelSection>
            <ClientDetailActions
              clientId={client._id}
              typePerson={client.typePerson}
              code={client.code}
              name={client.name}
              revalidationStatus={client.revalidationStatus}
              hasActiveFlow={client.hasActiveFlow}
              onNavigate={closePanel}
            />
          </SidePanelSection>

          <Tabs defaultValue="negotiations" className="w-full overflow-hidden">
            <SidePanelSection compact>
              <ScrollFade className="w-full" scrollClassName="overflow-y-hidden pb-1">
                <TabsList className="flex h-auto w-max min-w-full gap-1.5 rounded-none bg-transparent p-0 text-muted-foreground">
                  <TabsTrigger
                    value="negotiations"
                    className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    <Handshake className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-normal break-words">Negociaciones</span>
                  </TabsTrigger>
                  {/* Pestaña del PORTAL: detrás de su flag, igual que sus columnas y su hub. */}
                  {FEATURES.clientPortal ? (
                    <TabsTrigger
                      value="flows"
                      className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                    >
                      <GitBranch className="h-3.5 w-3.5 shrink-0" />
                      <span className="whitespace-normal break-words">Flujos</span>
                    </TabsTrigger>
                  ) : null}
                  <TabsTrigger
                    value="history"
                    className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    <History className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-normal break-words">Historial</span>
                  </TabsTrigger>
                </TabsList>
              </ScrollFade>
            </SidePanelSection>

            <SidePanelSection separated={false}>
              <TabsContent value="negotiations" className="mt-0">
                <ClientNegotiationsTab clientId={client._id} />
              </TabsContent>
              {FEATURES.clientPortal ? (
                <TabsContent value="flows" className="mt-0">
                  <ClientFlowsTab
                    clientId={client._id}
                    clientType={client.typePerson}
                    clientName={client.name}
                    // Jurídica soporta varios correos (array); el enlace se envía a UNO, así que
                    // se toma el primario. Individual ya es un único string. El array se conserva
                    // en el modelo; sólo aquí, donde hace falta un string, se coacciona.
                    clientEmail={
                      Array.isArray(client.email) ? (client.email[0] ?? null) : client.email
                    }
                    hasActiveFlow={client.hasActiveFlow}
                  />
                </TabsContent>
              ) : null}
              <TabsContent value="history" className="mt-0">
                {/* El `cli_`, que es con lo que la bitácora sella el `entityId`. Cae al `_id`
                    sólo si el documento no tiene backfill: ahí no encontrará nada, pero es el
                    comportamiento anterior y no rompe la pantalla. */}
                <ClientHistoryTab clientId={client.publicId ?? client._id} />
              </TabsContent>
            </SidePanelSection>
          </Tabs>
        </>
      )}
    </div>
  )
}
